import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { MapPin, Menu, Phone } from "lucide-react";
import { SITES } from "@/config/sites";
import { RMC_LOGO_IMAGE } from "@/config/imageSets";
import OptimizedImage from "@/components/site/OptimizedImage";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

// Build a proper tel: URI. Quebec numbers are +1-XXX-XXX-XXXX. Strip
// everything except digits, then prefix +1 if the number is 10 digits and
// doesn't already start with a country code. Without a country code, many
// desktop dialers silently no-op.
function telHref(phone) {
  if (!phone) return "#";
  const digits = phone.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return `tel:${digits}`;
  const bare = digits.replace(/\D/g, "");
  if (bare.length === 10) return `tel:+1${bare}`;
  if (bare.length === 11 && bare.startsWith("1")) return `tel:+${bare}`;
  return `tel:${bare}`;
}

export default function SiteHeader() {
  const { siteKey } = useParams();
  const site = SITES[siteKey];
  const { lang, setLang } = useLanguage();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  if (!site) return null;
  const isDark = site.theme.dark;
  const base = `/${siteKey}`;
  const linkFor = (pageKey) => {
    const p = site.pages[pageKey];
    if (!p) return base;
    return p.path ? `${base}/${p.path}` : base;
  };
  const isActive = (pageKey) => {
    const p = site.pages[pageKey];
    const target = p.path ? `${base}/${p.path}` : base;
    return location.pathname === target;
  };

  const navPages = Object.keys(site.pages).filter((k) => k !== "apply" && k !== "contact");

  // Top utility strip stays dark (premium look, matches reference site);
  // main nav bar is WHITE so the logos (which are designed for white bg) fit natively.
  const stripBg = isDark ? "#0A1428" : "rgba(15,23,42,0.9)";
  const navBg = "#FFFFFF";
  const navText = "#0F172A";
  const navMuted = "#475569";
  const navBorder = "rgba(15,23,42,0.08)";

  return (
    <header
      className="sticky top-0 z-40 w-full"
      style={{
        borderBottom: `1px solid ${navBorder}`,
      }}
      data-testid={`header-${siteKey}`}
    >
      {/* Top utility strip: address, phone, language */}
      <div
        className="hidden w-full text-white sm:block"
        style={{ backgroundColor: stripBg }}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 text-xs sm:px-6 lg:px-8">
          <div className="flex items-center gap-5 opacity-90">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {site.address || "Québec, Canada"}
            </span>
            <a href={telHref(site.phone)} className="inline-flex items-center gap-1.5 hover:opacity-80" data-testid="header-phone">
              <Phone className="h-3.5 w-3.5" />
              {site.phone}
            </a>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
              className="rounded-full border border-white/30 px-3 py-0.5 font-semibold uppercase tracking-wider hover:bg-white/10"
              data-testid="top-lang-toggle"
            >
              {lang === "fr" ? "EN" : "FR"}
            </button>
          </div>
        </div>
      </div>

      {/* Main nav — WHITE */}
      <div style={{ backgroundColor: navBg }}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 sm:py-5 lg:px-8">
        <Link to={base} className="flex items-center" data-testid="site-logo">
          {site.logo ? (
            <OptimizedImage
              {...RMC_LOGO_IMAGE}
              alt={site.name}
              className="h-16 w-auto object-contain sm:h-20 lg:h-24"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          ) : (
            <div className="flex items-center gap-3">
              <div
                className="flex h-11 w-11 items-center justify-center text-lg font-black text-white"
                style={{ backgroundColor: site.theme.primary, borderRadius: site.theme.radius }}
              >
                R
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-base font-black tracking-tight sm:text-lg" style={{ color: navText }}>
                  Rétablirmoncredit<span style={{ color: site.theme.primary }}>.com</span>
                </span>
                <span className="mt-0.5 hidden text-[10px] font-medium uppercase tracking-wider sm:inline" style={{ color: navMuted }}>
                  Financement automobile — Québec
                </span>
              </div>
            </div>
          )}
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="site-nav">
          {navPages.map((k) => (
            <Link
              key={k}
              to={linkFor(k)}
              data-testid={`nav-${k}`}
              className="relative text-sm font-semibold transition hover:opacity-70"
              style={{
                color: isActive(k) ? site.theme.primary : navText,
              }}
            >
              {lang === "fr" ? site.pages[k].labelFr : site.pages[k].labelEn}
              {isActive(k) && (
                <span
                  className="absolute -bottom-2 left-0 right-0 h-0.5"
                  style={{ backgroundColor: site.theme.primary }}
                />
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={telHref(site.phone)}
            className="hidden items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition hover:bg-slate-50 md:inline-flex"
            style={{
              borderColor: navBorder,
              color: navText,
            }}
            data-testid="nav-call-btn"
          >
            <Phone className="h-3.5 w-3.5" />
            {lang === "fr" ? "Appelez-nous" : "Call us"}
          </a>
          <Link to={`${base}/apply`}>
            <Button
              data-testid="header-apply-btn"
              className="hidden font-bold uppercase tracking-wider text-white sm:inline-flex"
              style={{
                backgroundColor: site.theme.primary,
                borderRadius: site.theme.radius,
              }}
            >
              {lang === "fr" ? "Vérifier mon admissibilité" : "Check my eligibility"}
            </Button>
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="menu-open-btn" variant="ghost" size="icon" className="lg:hidden">
                <Menu className="h-5 w-5" style={{ color: navText }} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] sm:w-[400px]" style={{ backgroundColor: "#FFFFFF", color: navText }}>
              <SheetTitle className="text-lg font-bold">{site.name}</SheetTitle>
              <div className="mt-6 flex flex-col gap-1">
                {Object.keys(site.pages).map((k) => (
                  <Link
                    key={k}
                    to={linkFor(k)}
                    data-testid={`mobile-nav-${k}`}
                    onClick={() => setOpen(false)}
                    className="rounded-md px-3 py-3 text-base font-semibold transition hover:bg-slate-50"
                    style={{ color: isActive(k) ? site.theme.primary : navText }}
                  >
                    {lang === "fr" ? site.pages[k].labelFr : site.pages[k].labelEn}
                  </Link>
                ))}
                <div className="mt-4 flex gap-2 px-3">
                  <button
                    type="button"
                    onClick={() => setLang("fr")}
                    data-testid="mobile-lang-fr"
                    className="rounded-full border px-4 py-2 text-sm font-semibold"
                    style={{
                      backgroundColor: lang === "fr" ? site.theme.primary : "transparent",
                      color: lang === "fr" ? "#fff" : navText,
                      borderColor: site.theme.primary,
                    }}
                  >
                    FR
                  </button>
                  <button
                    type="button"
                    onClick={() => setLang("en")}
                    data-testid="mobile-lang-en"
                    className="rounded-full border px-4 py-2 text-sm font-semibold"
                    style={{
                      backgroundColor: lang === "en" ? site.theme.primary : "transparent",
                      color: lang === "en" ? "#fff" : navText,
                      borderColor: site.theme.primary,
                    }}
                  >
                    EN
                  </button>
                </div>
                <Link to={`${base}/apply`} onClick={() => setOpen(false)} className="mt-4 px-3">
                  <Button
                    data-testid="mobile-apply-btn"
                    className="w-full font-bold uppercase text-white"
                    style={{ backgroundColor: site.theme.primary, borderRadius: site.theme.radius }}
                  >
                    {lang === "fr" ? "Vérifier mon admissibilité" : "Check my eligibility"}
                  </Button>
                </Link>
                <div className="mt-6 px-3 text-xs" style={{ color: navMuted }}>
                <a href={telHref(site.phone)} className="mt-1 flex items-center gap-2 hover:opacity-80" data-testid="mobile-phone-link">
                    <Phone className="h-3.5 w-3.5" /> {site.phone}
                  </a>
                  <div className="mt-1 flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5" /> {site.address}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      </div>
    </header>
  );
}
